import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'
import { axiosInstance } from '../helpers'
import { normalizeListResponse } from '../helpers/companyDocumentList'
import { OrganizationContractType } from '../models/Company/types'

const QUERY_ROOT = ['organizationContractTypes'] as const

export interface OrgContractTypeListParams {
    organization: number | null
    includeInactive?: boolean
}

export interface CreateOrganizationContractTypeInput {
    organization: number
    name: string
    description?: string | null
    required_company_ids?: number[]
    is_active?: boolean
}

export interface UpdateOrganizationContractTypeInput {
    name?: string
    description?: string | null
    required_company_ids?: number[]
    is_active?: boolean
}

const buildListQueryString = (params: OrgContractTypeListParams) => {
    if (!params.organization) return ''
    const searchParams = new URLSearchParams()
    searchParams.append('organization', String(params.organization))
    if (params.includeInactive) {
        searchParams.append('include_inactive', 'true')
    }
    const qs = searchParams.toString()
    return qs ? `?${qs}` : ''
}

export const useOrganizationContractTypes = (
    params: OrgContractTypeListParams,
) => {
    const queryClient = useQueryClient()
    const queryString = buildListQueryString(params)

    const { data: types, isLoading, error, refetch } = useQuery<
        OrganizationContractType[],
        Error
    >({
        queryKey: [...QUERY_ROOT, params.organization, params.includeInactive],
        queryFn: async () => {
            const res = await axiosInstance.get<unknown>(
                `/company/organization_contract_type/${queryString}`,
            )
            return normalizeListResponse<OrganizationContractType>(res.data)
        },
        enabled: !!params.organization,
        refetchOnMount: true,
        refetchOnWindowFocus: false,
    })

    const createType = useMutation({
        mutationFn: (body: CreateOrganizationContractTypeInput) =>
            axiosInstance.post<OrganizationContractType>(
                '/company/organization_contract_type/',
                body,
            ),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [...QUERY_ROOT] })
            toast.success('Contract type created')
        },
        onError: (error: any) => {
            const msg =
                error?.response?.data?.error ||
                error?.response?.data?.message ||
                'Failed to create contract type'
            toast.error(msg)
        },
    })

    const updateType = useMutation({
        mutationFn: ({
            id,
            body,
        }: {
            id: number
            body: UpdateOrganizationContractTypeInput
        }) =>
            axiosInstance.patch<OrganizationContractType>(
                `/company/organization_contract_type/${id}/`,
                body,
            ),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [...QUERY_ROOT] })
            toast.success('Contract type updated')
        },
        onError: (error: any) => {
            const msg =
                error?.response?.data?.error ||
                error?.response?.data?.message ||
                'Failed to update contract type'
            toast.error(msg)
        },
    })

    const deleteType = useMutation({
        mutationFn: (id: number) =>
            axiosInstance.delete(`/company/organization_contract_type/${id}/`),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [...QUERY_ROOT] })
            toast.success('Contract type deleted')
        },
        onError: (error: any) => {
            const msg =
                error?.response?.data?.error ||
                error?.response?.data?.message ||
                'Failed to delete contract type'
            toast.error(msg)
        },
    })

    const sortedTypes = [...(types ?? [])].sort((a, b) =>
        a.name.localeCompare(b.name),
    )

    return {
        contractTypes: sortedTypes,
        isLoading,
        error,
        refetch,
        createType,
        updateType,
        deleteType,
        isCreating: createType.isLoading,
        isUpdating: updateType.isLoading,
        isDeleting: deleteType.isLoading,
    }
}
